import React, { useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import EditorPanelIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import type { globalNotificationsConfigTable_personalSettingsNotificationsConfigTable_GlobalNotificationConfigTableNew$key } from '@atlassian/jira-relay/src/__generated__/globalNotificationsConfigTable_personalSettingsNotificationsConfigTable_GlobalNotificationConfigTableNew.graphql.ts';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { EMAIL, IN_PRODUCT } from '../../common/constants.tsx';
import type { HeadCheckboxIdsType } from '../../common/types.tsx';
import messages from '../../common/ui/messages.tsx';
import { NotificationsConfigRow as NotificationsConfigRowAccordion } from '../../common/ui/notifications-accordian-row/index.tsx';
import { HeadCheckbox } from '../../common/ui/notifications-config-head/index.tsx';
import { HeadCellCheckboxContainer } from '../../common/ui/notifications-config-head/styled.tsx';
import { NotificationsConfigRow } from '../../common/ui/notifications-config-row/index.tsx';
import {
	Table,
	TableHeadContainer,
	TableHeadRowContainer,
	HeadCellContainer,
	TBody,
} from '../../common/ui/styled.tsx';
import { useGlobalHeadMutation } from './global-notifications-config-head/index.tsx';
import { useGlobalRowMutation } from './global-notifications-config-row/index.tsx';

export const GlobalNotificationConfigTableNew = ({
	allPreferencesData,
}: {
	allPreferencesData: globalNotificationsConfigTable_personalSettingsNotificationsConfigTable_GlobalNotificationConfigTableNew$key;
}) => {
	const { formatMessage } = useIntl();
	const globalHeadMutationInProduct = useGlobalHeadMutation({ channel: IN_PRODUCT });
	const globalHeadMutationEmail = useGlobalHeadMutation({ channel: EMAIL });
	const globalRowMutation = useGlobalRowMutation();
	const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
	const allPreferences = useFragment(
		graphql`
			fragment globalNotificationsConfigTable_personalSettingsNotificationsConfigTable_GlobalNotificationConfigTableNew on JiraNotificationGlobalPreference {
				...notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_enabledDataFragment
				...notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_enabledDataFragment
				...notificationsConfigRow_personalSettingsNotificationsConfigTable_enabledDataFragment
				preferences @required(action: THROW) {
					...notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_allPreferences
					...notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_allPreferences
					...notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences

					issueAssigned @required(action: THROW) {
						...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
					}
					mentionsCombined @required(action: THROW) {
						...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
					}
					worklogCombined @required(action: THROW) {
						...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
					}
					miscellaneousIssueEventCombined @required(action: THROW) {
						...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
					}
				}
			}
		`,
		allPreferencesData,
	);

	const { preferences } = allPreferences;

	const emailGlobalHeadCheckboxId = 'email';
	const inProductGlobalHeadCheckboxId = 'inProduct';

	const headCheckboxIds: HeadCheckboxIdsType = {
		EMAIL: emailGlobalHeadCheckboxId,
		IN_PRODUCT: inProductGlobalHeadCheckboxId,
	};

	return (
		<ContextualAnalyticsData sourceName="personalSettingsGlobalNotifications" sourceType={SCREEN}>
			<>
				<DefaultNotificationHeader>
					{formatMessage(
						expVal('spike-itr-rollout-strategy', 'is_enabled', false)
							? messages.defaultNotificationSettings
							: messages.defaultNotificationSettings,
					)}
				</DefaultNotificationHeader>
				<DefaultNotificationHeaderDescription>
					{formatMessage(messages.defaultNotificationSettingsDescription)}
				</DefaultNotificationHeaderDescription>
			</>
			<GlobalContainer>
				<Box xcss={tableWrapperStyles}>
					<Table>
						<TableHeadContainer>
							<TableHeadRowContainer>
								<HeadCellContainer>
									{formatMessage(
										fg('jira-issue-terminology-refresh-m3')
											? messages.notificationColumnHeaderAllIssuesIssueTermRefresh
											: messages.notificationColumnHeaderAllIssues,
									)}
								</HeadCellContainer>
								<HeadCellCheckboxContainer>
									<HeadCheckbox
										id={`all-issues-${inProductGlobalHeadCheckboxId}`}
										commitMutation={globalHeadMutationInProduct}
										channel={IN_PRODUCT}
										enabledDataFragment={allPreferences}
										label={formatMessage(messages.inProductColumnHeader)}
										allPreferences={preferences}
										categories={['ISSUE_ASSIGNED', 'ISSUE_MENTIONED']}
									/>
								</HeadCellCheckboxContainer>
								<HeadCellCheckboxContainer>
									<HeadCheckbox
										id={`all-issues-${emailGlobalHeadCheckboxId}`}
										commitMutation={globalHeadMutationEmail}
										channel={EMAIL}
										enabledDataFragment={allPreferences}
										label={formatMessage(messages.emailColumnHeader)}
										allPreferences={preferences}
										categories={['ISSUE_ASSIGNED', 'ISSUE_MENTIONED']}
									/>
								</HeadCellCheckboxContainer>
							</TableHeadRowContainer>
						</TableHeadContainer>
						<TBody>
							<NotificationsConfigRow
								preference={preferences.issueAssigned}
								allPreferences={preferences}
								enabledDataFragment={allPreferences}
								label={formatMessage(
									fg('jira-issue-terminology-refresh-m3')
										? messages.issueAssignedTypeIssueTermRefresh
										: messages.issueAssignedType,
								)}
								id="issueAssigned"
								headCheckboxIds={headCheckboxIds}
								commitMutation={globalRowMutation}
							/>
							<NotificationsConfigRow
								preference={preferences.mentionsCombined}
								allPreferences={preferences}
								enabledDataFragment={allPreferences}
								label={formatMessage(
									fg('jira-issue-terminology-refresh-m3')
										? messages.mentionsCombinedTypeIssueTermRefresh
										: messages.mentionsCombinedType,
								)}
								id="mentionsCombined"
								headCheckboxIds={headCheckboxIds}
								commitMutation={globalRowMutation}
							/>
						</TBody>
					</Table>
				</Box>
				<Box xcss={tableWrapperStyles}>
					<Table>
						<TableHeadContainer>
							<TableHeadRowContainer>
								<HeadCellContainer>
									<Inline alignBlock="center">
										{formatMessage(
											fg('jira-issue-terminology-refresh-m3')
												? messages.notificationColumnHeaderRelevantIssuesIssueTermRefresh
												: messages.notificationColumnHeaderRelevantIssues,
										)}
										<Popup
											placement="right-start"
											messageId="personal-settings-notifications-config-table.ui.global-notifications-config-table.popup"
											messageType="transactional"
											isOpen={isInfoPanelOpen}
											onClose={() => {
												setIsInfoPanelOpen(false);
											}}
											content={() => (
												<Box padding="space.200">
													{formatMessage(
														fg('jira-issue-terminology-refresh-m3')
															? messages.relevantIssueNotificationTableInfoIconTextIssueTermRefresh
															: messages.relevantIssueNotificationTableInfoIconText,
													)}
												</Box>
											)}
											trigger={({ 'aria-expanded': _, ...triggerProps }) => (
												<Box
													xcss={infoIconStyles}
													onMouseEnter={() => {
														setIsInfoPanelOpen(true);
													}}
													onMouseLeave={() => {
														setIsInfoPanelOpen(false);
													}}
													testId="personal-settings-notifications-config-table.ui.global-notifications-config-table.warning-pop-up-button-container"
													// eslint-disable-next-line react/jsx-props-no-spreading
													{...triggerProps}
												>
													<EditorPanelIcon
														spacing="spacious"
														label="warning"
														color={token('color.icon.accent.gray')}
													/>
												</Box>
											)}
										/>
									</Inline>
								</HeadCellContainer>
								<HeadCellCheckboxContainer>
									<HeadCheckbox
										id={`relevant-issues-${inProductGlobalHeadCheckboxId}`}
										commitMutation={globalHeadMutationInProduct}
										channel={IN_PRODUCT}
										enabledDataFragment={allPreferences}
										label={formatMessage(messages.inProductColumnHeader)}
										allPreferences={preferences}
										categories={[
											'ISSUE_CHANGES',
											'COMMENT_CHANGES',
											'ISSUE_WORKLOG_CHANGES',
											'ISSUE_MISCELLANEOUS',
										]}
									/>
								</HeadCellCheckboxContainer>
								<HeadCellCheckboxContainer>
									<HeadCheckbox
										id={`relevant-issues-${emailGlobalHeadCheckboxId}`}
										commitMutation={globalHeadMutationEmail}
										channel={EMAIL}
										enabledDataFragment={allPreferences}
										label={formatMessage(messages.emailColumnHeader)}
										allPreferences={preferences}
										categories={[
											'ISSUE_CHANGES',
											'COMMENT_CHANGES',
											'ISSUE_WORKLOG_CHANGES',
											'ISSUE_MISCELLANEOUS',
										]}
									/>
								</HeadCellCheckboxContainer>
							</TableHeadRowContainer>
						</TableHeadContainer>
						<TBody>
							<NotificationsConfigRowAccordion
								allPreferences={preferences}
								enabledDataFragment={allPreferences}
								label={formatMessage(
									fg('jira-issue-terminology-refresh-m3')
										? messages.issueChangesCategoryTypeIssueTermRefresh
										: messages.issueChangesCategoryType,
								)}
								id="issueChanges"
								headCheckboxIds={headCheckboxIds}
								onRowMutation={globalRowMutation}
								onEmailHeadMutation={globalHeadMutationEmail}
								onInProductHeadMutation={globalHeadMutationInProduct}
								category="ISSUE_CHANGES"
							/>
							<NotificationsConfigRowAccordion
								allPreferences={preferences}
								enabledDataFragment={allPreferences}
								label={formatMessage(
									fg('jira-issue-terminology-refresh-m3')
										? messages.commentChangesCategoryTypeIssueTermRefresh
										: messages.commentChangesCategoryType,
								)}
								id="commentChanges"
								headCheckboxIds={headCheckboxIds}
								onRowMutation={globalRowMutation}
								onEmailHeadMutation={globalHeadMutationEmail}
								onInProductHeadMutation={globalHeadMutationInProduct}
								category="COMMENT_CHANGES"
							/>
							<NotificationsConfigRow
								preference={preferences.worklogCombined}
								allPreferences={preferences}
								enabledDataFragment={allPreferences}
								label={formatMessage(messages.worklogCombinedType)}
								id="worklogCombined"
								headCheckboxIds={headCheckboxIds}
								commitMutation={globalRowMutation}
							/>
							<NotificationsConfigRow
								preference={preferences.miscellaneousIssueEventCombined}
								allPreferences={preferences}
								enabledDataFragment={allPreferences}
								label={formatMessage(
									fg('jira-issue-terminology-refresh-m3')
										? messages.miscellaneousIssueEventCombinedIssueTermRefresh
										: messages.miscellaneousIssueEventCombined,
								)}
								id="miscellaneousIssueEventCombined"
								headCheckboxIds={headCheckboxIds}
								commitMutation={globalRowMutation}
							/>
						</TBody>
					</Table>
				</Box>
			</GlobalContainer>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GlobalContainer = styled.div({
	boxSizing: 'border-box',
	marginTop: token('space.100'),
	marginBottom: token('space.300'),
	borderRadius: token('space.050'),
	backgroundColor: token('elevation.surface.sunken'),
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DefaultNotificationHeader = styled.h5({
	display: 'flex',
});

const infoIconStyles = xcss({
	marginLeft: 'space.100',
	marginTop: 'space.025',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DefaultNotificationHeaderDescription = styled.div({
	display: 'flex',
});

const tableWrapperStyles = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: 'border.radius.050',
	marginBottom: 'space.200',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		marginBottom: 'space.0',
	},
});
